import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Eye, Pencil, Trash2, Plus, BarChart2 } from "lucide-react";
import { useApiService } from "../../services/apiService";
import type { NavigationItem } from "../Navigation";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import type { ConceptSummary } from "../../types/concept";

interface ConceptDashboardProps {
  onNavigate: (view: NavigationItem, campaignId?: string, conceptId?: number) => void;
}

const decisionStageVariant = (stage: string) => {
  const normalized = stage?.toLowerCase() ?? "";
  if (normalized.includes("idea")) return "secondary" as const;
  if (normalized.includes("validated")) return "outline" as const;
  if (normalized.includes("ready")) return "default" as const;
  if (normalized.includes("concept")) return "default" as const;
  return "outline" as const;
};

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
};

const truncate = (value: string, length = 60) => {
  if (!value) return "—";
  return value.length > length ? `${value.slice(0, length)}…` : value;
};

export function ConceptDashboard({ onNavigate }: ConceptDashboardProps) {
  const api = useApiService();
  const [concepts, setConcepts] = useState<ConceptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchConcepts = useCallback(
    async (showInitialLoader = false) => {
      if (showInitialLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        const data = await api.get<ConceptSummary[]>("/concepts");
        setConcepts(data);
        setError(null);
      } catch (err) {
        console.error("Failed to load concepts", err);
        setError(err instanceof Error ? err.message : "Unable to load concepts");
      } finally {
        if (showInitialLoader) {
          setLoading(false);
        }
        setRefreshing(false);
      }
    },
    [api]
  );

  useEffect(() => {
    void fetchConcepts(true);
  }, [fetchConcepts]);

  const stageMetrics = useMemo(() => {
    const metrics = {
      total: concepts.length,
      idea: 0,
      concept: 0,
      validated: 0,
      ready: 0,
      other: 0,
    };

    for (const concept of concepts) {
      const stage = (concept.decisionStage || "").toLowerCase();
      if (stage.includes("idea")) metrics.idea += 1;
      else if (stage.includes("validated")) metrics.validated += 1;
      else if (stage.includes("ready")) metrics.ready += 1;
      else if (stage.includes("concept")) metrics.concept += 1;
      else metrics.other += 1;
    }

    return metrics;
  }, [concepts]);

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await api.delete(`/concepts/${id}`);
      toast.success("Concept deleted", {
        description: "The concept and its metadata have been removed from the portfolio.",
      });
      await fetchConcepts();
    } catch (err) {
      console.error("Failed to delete concept", err);
      toast.error("Unable to delete concept", {
        description: err instanceof Error ? err.message : "Please try again later.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-foreground">Concept Management</h1>
          <p className="text-muted-foreground max-w-2xl">
            Capture, evaluate, and iterate on product concepts with a structured metadata set that keeps marketing, R&D, and
            commercial teams aligned.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchConcepts()}
            disabled={loading || refreshing}
            className="flex items-center gap-2"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Refresh
          </Button>
          <CapabilityButton capability={P.CampaignConceptCreate} size="sm" className="flex items-center gap-2" onClick={() => onNavigate("create-concept")}>
            <Plus className="h-4 w-4" />
            New Concept
          </CapabilityButton>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Concepts</CardTitle>
            <BarChart2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stageMetrics.total}</div>
            <p className="text-xs text-muted-foreground">Tracked across the entire lifecycle</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Idea Stage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stageMetrics.idea}</div>
            <p className="text-xs text-muted-foreground">Early hypotheses under exploration</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">In Concept</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stageMetrics.concept}</div>
            <p className="text-xs text-muted-foreground">Being shaped for validation</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Validated</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stageMetrics.validated}</div>
            <p className="text-xs text-muted-foreground">Ready for pilot or launch assessment</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Launch Ready</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{stageMetrics.ready}</div>
            <p className="text-xs text-muted-foreground">Prepared for TG1/TG2 style approval</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle>Concept Portfolio</CardTitle>
            <CardDescription>
              Monitor concept health, quickly access detailed dossiers, and collaborate with cross-functional teams.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="text-sm">Loading concept portfolio…</span>
            </div>
          ) : (
            <div className="space-y-4">
              {error && (
                <div className="border border-destructive/40 bg-destructive/5 text-destructive rounded-md p-4 text-sm">
                  <p className="font-medium mb-1">We couldn't refresh concepts.</p>
                  <p className="mb-2">{error}</p>
                  <Button variant="outline" size="sm" onClick={() => fetchConcepts()}>
                    Try again
                  </Button>
                </div>
              )}

              {concepts.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                  <p className="font-medium text-base text-foreground mb-2">No concepts captured yet</p>
                  <p className="text-sm mb-4">
                    Start by creating your first concept to capture positioning, customer insights, and go-to-market plans.
                  </p>
                  <CapabilityButton capability={P.CampaignConceptCreate} onClick={() => onNavigate("create-concept")}>Create a concept</CapabilityButton>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[180px]">Concept</TableHead>
                        <TableHead className="min-w-[140px]">Product Type</TableHead>
                        <TableHead className="min-w-[200px]">Target Audience</TableHead>
                        <TableHead className="min-w-[220px]">Value Proposition</TableHead>
                        <TableHead className="min-w-[160px]">Decision Stage</TableHead>
                        <TableHead className="min-w-[140px]">Last Updated</TableHead>
                        <TableHead className="w-[120px] text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {concepts.map((concept) => (
                        <TableRow key={concept.id}>
                          <TableCell>
                            <div className="font-medium text-foreground">{concept.title}</div>
                            <div className="text-xs text-muted-foreground">Created {formatDate(concept.createdAt)}</div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{concept.productType || "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{truncate(concept.targetAudience)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {truncate(concept.coreValueProposition)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={decisionStageVariant(concept.decisionStage)} className="capitalize">
                              {concept.decisionStage || "Unspecified"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{formatDate(concept.updatedAt)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onNavigate("concept-detail", undefined, concept.id)}
                                title="View concept details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <CapabilityButton capability={P.CampaignConceptUpdate}
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => onNavigate("edit-concept", undefined, concept.id)}
                                title="Edit concept"
                              >
                                <Pencil className="h-4 w-4" />
                              </CapabilityButton>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <CapabilityButton capability={P.CampaignConceptDelete}
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    disabled={deletingId === concept.id}
                                    title="Delete concept"
                                  >
                                    {deletingId === concept.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </CapabilityButton>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete concept</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently remove "{concept.title}" and all related metadata. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <CapabilityButton capability={P.CampaignConceptDelete} component={AlertDialogAction}
                                      onClick={() => handleDelete(concept.id)}
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      disabled={deletingId === concept.id}
                                    >
                                      Delete concept
                                    </CapabilityButton>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
